import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Key, Shield, Unlock, ShieldCheck } from 'lucide-react';
import UserCapabilitiesModal from '../components/UserCapabilitiesModal';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCapabilitiesUser, setShowCapabilitiesUser] = useState(null);
  const [showAdminResetModal, setShowAdminResetModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 'accountant'
  });
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [adminResetData, setAdminResetData] = useState({
    new_password: '',
    confirm_password: '',
    force_change_on_next_login: true,
  });

  const roles = [
    { value: 'admin', label: 'Admin', color: 'red' },
    { value: 'pilot_admin', label: 'Pilot Admin', color: 'fuchsia' },
    { value: 'home', label: 'Home', color: 'rose' },
    { value: 'manager', label: 'Manager', color: 'purple' },
    { value: 'accountant', label: 'Accountant', color: 'blue' },
    { value: 'project_manager', label: 'Project Manager', color: 'green' },
    { value: 'sales_rep', label: 'Sales Rep', color: 'orange' },
    { value: 'technical', label: 'Technical', color: 'cyan' },
    { value: 'hr', label: 'HR', color: 'pink' },
    { value: 'engineer', label: 'Engineer', color: 'indigo' },
    { value: 'assistant', label: 'Assistant', color: 'teal' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          full_name: formData.full_name,
          username: formData.username,
          email: formData.email,
          phone: formData.phone,
          role: formData.role
        };
        // Include password only if provided
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }
        await axiosInstance.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        await axiosInstance.post('/auth/register', formData);
        toast.success('User created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axiosInstance.delete(`/users/${id}`);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name,
      username: user.username,
      email: user.email,
      phone: user.phone || '',
      password: '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axiosInstance.put(`/users/${id}/status?status=${status}`);
      toast.success('Status updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      await axiosInstance.put(`/users/${selectedUser.id}/password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setSelectedUser(null);
      // Refresh users list to show updated password
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      role: 'accountant'
    });
    setEditingUser(null);
  };

  const getRoleColor = (role) => {
    const roleObj = roles.find(r => r.value === role);
    return roleObj ? roleObj.color : 'gray';
  };

  if (loading) {
    return <div className="text-center py-12">Loading users...</div>;
  }

  return (
    <div data-testid="users-page">
      <div className="page-header">
        <h2 className="page-title">Users Management</h2>
        <button
          data-testid="add-user-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New User
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Full Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user) => (
                <tr key={user.id} data-testid={`user-row-${user.id}`}>
                  <td className="font-semibold">{user.user_number}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold`}>
                        {user.full_name.charAt(0)}
                      </div>
                      <span className="font-semibold">{user.full_name}</span>
                    </div>
                  </td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.phone || '-'}</td>
                  <td>
                    <span className={`status-badge ${getRoleColor(user.role)}`}>
                      {roles.find(r => r.value === user.role)?.label}
                    </span>
                  </td>
                  <td>
                    <select
                      value={user.status}
                      onChange={(e) => handleStatusChange(user.id, e.target.value)}
                      className={`status-badge ${user.status} cursor-pointer`}
                      style={{ border: 'none', outline: 'none' }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td>
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleString()
                      : 'Never'
                    }
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        data-testid={`change-password-${user.id}`}
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordModal(true);
                        }}
                        className="btn-icon"
                        title="Change Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-reset-${user.id}`}
                        onClick={() => {
                          setSelectedUser(user);
                          setAdminResetData({ new_password: '', confirm_password: '', force_change_on_next_login: true });
                          setShowAdminResetModal(true);
                        }}
                        className="btn-icon"
                        title="Admin Reset Password"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      {user.locked_until && new Date(user.locked_until) > new Date() && (
                        <button
                          data-testid={`unlock-user-${user.id}`}
                          onClick={async () => {
                            try {
                              await axiosInstance.post(`/users/${user.id}/unlock`);
                              toast.success('User unlocked');
                              fetchUsers();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Unlock failed');
                            }
                          }}
                          className="btn-icon"
                          title="Unlock Account"
                        >
                          <Unlock className="w-4 h-4 text-orange-600" />
                        </button>
                      )}
                      <button
                        data-testid={`capabilities-user-${user.id}`}
                        onClick={() => setShowCapabilitiesUser(user)}
                        className="btn-icon"
                        title="الصلاحيات الاستثنائية"
                      >
                        <ShieldCheck className="w-4 h-4 text-violet-600" />
                      </button>
                      <button
                        data-testid={`edit-user-${user.id}`}
                        onClick={() => handleEdit(user)}
                        className="btn-icon edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-user-${user.id}`}
                        onClick={() => handleDelete(user.id)}
                        className="btn-icon delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-12 text-gray-500">
                  No users found. Add your first user!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingUser ? 'Edit User' : 'New User'}
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {editingUser && (
                <div className="form-group">
                  <label className="form-label">User ID</label>
                  <input
                    type="text"
                    value={editingUser.user_number}
                    className="form-input bg-gray-100"
                    disabled
                    readOnly
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  data-testid="user-fullname-input"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="form-input"
                  placeholder="Enter full name"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Username *</label>
                <input
                  data-testid="user-username-input"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="form-input"
                  placeholder="Enter username"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  data-testid="user-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                  placeholder="Optional"
                />
              </div>

              {!editingUser && (
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input
                    data-testid="user-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="form-input"
                    placeholder="Enter password"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">كلمة المرور للدخول إلى البرنامج</p>
                </div>
              )}

              {editingUser && (
                <div className="form-group">
                  <label className="form-label">New Password (Optional)</label>
                  <input
                    data-testid="user-new-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="form-input"
                    placeholder="Leave empty to keep current password"
                  />
                  <p className="text-xs text-gray-500 mt-1">اتركه فارغاً للإبقاء على كلمة المرور الحالية</p>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Role *</label>
                <select
                  data-testid="user-role-select"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="form-select"
                  required
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-user-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleChangePassword}>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Changing password for: <strong>{selectedUser?.full_name}</strong>
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Old Password *</label>
                <input
                  type="password"
                  value={passwordData.old_password}
                  onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">New Password *</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password *</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Reset Password Modal */}
      {showAdminResetModal && (
        <div className="modal-overlay" onClick={() => setShowAdminResetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Admin: Reset User Password</h3>
              <button
                data-testid="close-admin-reset-modal"
                onClick={() => setShowAdminResetModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (adminResetData.new_password !== adminResetData.confirm_password) {
                toast.error('Passwords do not match');
                return;
              }
              try {
                await axiosInstance.post(`/users/${selectedUser.id}/reset-password`, {
                  new_password: adminResetData.new_password,
                  force_change_on_next_login: adminResetData.force_change_on_next_login,
                });
                toast.success('Password reset successfully. Share it with the user securely — it will not be shown again.');
                setShowAdminResetModal(false);
                setAdminResetData({ new_password: '', confirm_password: '', force_change_on_next_login: true });
                setSelectedUser(null);
                fetchUsers();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to reset password');
              }
            }}>
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <Shield className="w-4 h-4 inline mr-1" />
                Resetting password for <strong>{selectedUser?.full_name}</strong>.
                You can SET a new password but you can never SEE the old one — passwords are stored as one-way hashes.
              </div>

              <div className="form-group">
                <label className="form-label">New Password *</label>
                <input
                  data-testid="admin-reset-new-password"
                  type="password"
                  value={adminResetData.new_password}
                  onChange={(e) => setAdminResetData({ ...adminResetData, new_password: e.target.value })}
                  className="form-input"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be 8+ chars with uppercase, number, and a special character.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password *</label>
                <input
                  data-testid="admin-reset-confirm-password"
                  type="password"
                  value={adminResetData.confirm_password}
                  onChange={(e) => setAdminResetData({ ...adminResetData, confirm_password: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminResetData.force_change_on_next_login}
                    onChange={(e) => setAdminResetData({ ...adminResetData, force_change_on_next_login: e.target.checked })}
                  />
                  <span className="text-sm">Force user to change password on next login</span>
                </label>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-admin-reset"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdminResetModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Capabilities Modal */}
      {showCapabilitiesUser && (
        <UserCapabilitiesModal
          user={showCapabilitiesUser}
          onClose={() => setShowCapabilitiesUser(null)}
        />
      )}
    </div>
  );
};

export default Users;
